import { useState } from "react";
import { useProfessorSchedules, ProfessorScheduleEntry } from "@/hooks/schedules/useProfessorSchedules";
import { useGymMembers } from "@/hooks/schedules/useGymMembers";
import { useProfile } from "@/hooks/useProfile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Clock, User, Calendar } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const DAY_NAMES: Record<number, string> = {
  1: "Lunes",
  2: "Martes",
  3: "Miércoles",
  4: "Jueves",
  5: "Viernes",
  6: "Sábado",
};

export const ProfessorScheduleManager = () => {
  const { profile } = useProfile();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: schedules, isLoading } = useProfessorSchedules(profile?.gym_id);
  const { data: members } = useGymMembers(profile?.gym_id);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [startTime, setStartTime] = useState("18:00");
  const [endTime, setEndTime] = useState("19:00");
  const [submitting, setSubmitting] = useState(false);

  const groupedByDay = (schedules || []).reduce<Record<number, ProfessorScheduleEntry[]>>((acc, s) => {
    if (!acc[s.day_of_week]) acc[s.day_of_week] = [];
    acc[s.day_of_week].push(s);
    return acc;
  }, {});

  const handleAdd = async () => {
    if (!selectedMember || !selectedDay || !startTime || !endTime || !user || !profile?.gym_id) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.from("member_schedules").insert({
        member_id: selectedMember,
        professor_id: user.id,
        gym_id: profile.gym_id,
        day_of_week: parseInt(selectedDay),
        start_time: startTime,
        end_time: endTime,
      });

      if (error) throw error;

      toast({ title: "Turno agregado", description: "El turno fue creado exitosamente." });
      queryClient.invalidateQueries({ queryKey: ["professor-schedules"] });
      setDialogOpen(false);
      resetForm();
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from("member_schedules")
        .update({ is_active: false })
        .eq("id", id);

      if (error) throw error;

      toast({ title: "Turno eliminado" });
      queryClient.invalidateQueries({ queryKey: ["professor-schedules"] });
    } catch (err: any) {
      toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const resetForm = () => {
    setSelectedMember("");
    setSelectedDay("");
    setStartTime("18:00");
    setEndTime("19:00");
  };

  const getInitials = (first?: string | null, last?: string | null) => {
    return `${(first || "")[0] || ""}${(last || "")[0] || ""}`.toUpperCase() || "?";
  };

  if (isLoading) {
    return <div className="text-center text-muted-foreground py-8">Cargando horarios...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">Mis Horarios Asignados</h2>
        <Button onClick={() => setDialogOpen(true)} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          Agregar turno
        </Button>
      </div>

      {Object.keys(groupedByDay).length === 0 ? (
        <div className="text-center text-muted-foreground py-12 bg-card rounded-xl border border-border">
          <Calendar className="h-10 w-10 mx-auto mb-3 opacity-50" />
          <p>No tenés turnos asignados todavía.</p>
          <p className="text-sm mt-1">Agregá un turno para empezar.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedByDay)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([day, entries]) => (
              <div key={day} className="bg-card rounded-xl border border-border overflow-hidden">
                <div className="px-4 py-3 bg-primary/10 border-b border-border">
                  <h3 className="font-semibold text-foreground flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    {DAY_NAMES[Number(day)] || `Día ${day}`}
                  </h3>
                </div>
                <div className="divide-y divide-border">
                  {entries.map((entry) => (
                    <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={entry.member?.avatar_url || undefined} />
                          <AvatarFallback className="text-xs bg-primary/20 text-primary">
                            {getInitials(entry.member?.first_name, entry.member?.last_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {entry.member?.first_name || "Sin nombre"} {entry.member?.last_name || ""}
                          </p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {entry.start_time.slice(0, 5)} - {entry.end_time.slice(0, 5)}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(entry.id)}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Agregar turno</DialogTitle>
            <DialogDescription>Asigná un socio a un día y horario.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Socio</Label>
              <Select value={selectedMember} onValueChange={setSelectedMember}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar socio" />
                </SelectTrigger>
                <SelectContent>
                  {(members || []).map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="flex items-center gap-2">
                        <User className="h-3 w-3" />
                        {m.first_name || "Sin nombre"} {m.last_name || ""}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Día</Label>
              <Select value={selectedDay} onValueChange={setSelectedDay}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar día" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DAY_NAMES).map(([val, name]) => (
                    <SelectItem key={val} value={val}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Hora inicio</Label>
                <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Hora fin</Label>
                <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </div>
            </div>

            <Button
              onClick={handleAdd}
              disabled={!selectedMember || !selectedDay || !startTime || !endTime || submitting}
              className="w-full"
            >
              {submitting ? "Guardando..." : "Agregar turno"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
